import React, { useEffect, useState, useRef } from "react";
import Lottie from "lottie-react";
import loader from "../assets/loader.json";
import Graph from "./Graph";
import PieChart from "./PieChart";
import { useUserListMutation } from "../redux/features/user/userApi";
import { useDeleteUserMutation } from "../redux/features/user/userApi";
import { DataGrid } from "@mui/x-data-grid";
import { useMediaQuery } from "@mui/material";
import { FaEdit } from "react-icons/fa";
import { BiExport } from "react-icons/bi";
import { MdDelete } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { styles } from "../styles/style";
import { useGetAllNftQuery } from "../redux/features/user/userApi";

// ConfirmModal component
const ConfirmModal = ({ open, onCancel, onConfirm, message }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm">
        <h2 className="text-xl font-bold mb-2 text-red-600">Delete User</h2>
        <p className="mb-4 text-gray-700">{message}</p>
        <div className="flex justify-end gap-2">
          <button
            className="px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
            onClick={onConfirm}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Toast component
const Toast = ({ open, message, onClose }) => {
  useEffect(() => {
    if (open) {
      const timer = setTimeout(onClose, 2500);
      return () => clearTimeout(timer);
    }
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed top-5 right-5 bg-green-500 text-white px-4 py-2 rounded shadow-lg flex items-center gap-2 z-50">
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
      <span>{message}</span>
      <button onClick={onClose} className="ml-2 text-white hover:text-gray-200">&times;</button>
    </div>
  );
};

const Blocks = () => {
  const [userList, { isLoading, isSuccess, data: userData }] =
    useUserListMutation();
  const [deleteUser] = useDeleteUserMutation();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [active, setActive] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const targetElement = useRef();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const { data: nftData, isLoading: nftLoading } = useGetAllNftQuery();
  const [selectedDate, setSelectedDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const isMobile = useMediaQuery("(max-width:640px)");

  // Normalize various date formats to YYYY-MM-DD
  const normalizeDate = (input) => {
    if (!input) return "";
    if (typeof input === 'string') {
      const trimmed = input.trim();
      // Already YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
      // DD-MM-YYYY or DD/MM/YYYY
      const m = trimmed.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})$/);
      if (m) {
        const [_, d, mo, y] = m;
        return `${y}-${mo}-${d}`;
      }
      // Try Date parsing fallback
      const dObj = new Date(trimmed);
      if (!isNaN(dObj)) return new Date(dObj.getTime() - dObj.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    }
    if (input instanceof Date && !isNaN(input)) {
      return new Date(input.getTime() - input.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    }
    return "";
  };

  // Extract unique normalized dates from NFT history (supporting history as array)
  let uniqueDates = [];
  if (nftData && nftData.status && Array.isArray(nftData.data)) {
    const dateSet = new Set();
    nftData.data.forEach((item) => {
      if (Array.isArray(item.history)) {
        item.history.forEach(h => {
          const nd = normalizeDate(h.date);
          if (nd) dateSet.add(nd);
        });
      } else if (item.history?.date) {
        const nd = normalizeDate(item.history.date);
        if (nd) dateSet.add(nd);
      }
    });
    uniqueDates = Array.from(dateSet).sort(); // ascending YYYY-MM-DD
  }

  // Aggregate perunit by plantname for selectedDate (supporting history as array)
  let pieData = [];
  if (nftData && nftData.status && Array.isArray(nftData.data)) {
    const plantMap = {};
    nftData.data.forEach((item) => {
      if (Array.isArray(item.history)) {
        item.history.forEach(h => {
          const nd = normalizeDate(h.date);
          if ((!selectedDate || nd === selectedDate) && h.plantname) {
            const plant = h.plantname;
            const perunit = h.perunit || 0;
            if (plantMap[plant]) {
              plantMap[plant] += perunit;
            } else {
              plantMap[plant] = perunit;
            }
          }
        });
      } else if (item.history) {
        const nd = normalizeDate(item.history.date);
        if (!selectedDate || nd === selectedDate) {
          const plant = item.history.plantname || "Unknown";
          const perunit = item.history.perunit || 0;
          if (plantMap[plant]) {
            plantMap[plant] += perunit;
          } else {
            plantMap[plant] = perunit;
          }
        }
      }
    });
    pieData = Object.entries(plantMap).map(([plantname, value]) => ({ plantname, value }));
  }

  // Set default selectedDate to most recent available date
  React.useEffect(() => {
    if (!selectedDate && uniqueDates.length > 0) {
      setSelectedDate(uniqueDates[uniqueDates.length - 1]);
    }
  }, [uniqueDates, selectedDate]);

  const scrollingToTable = () => {
    targetElement.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "start",
    });
  };

  const activeUsers = data.filter((dt) => dt.isActive);
  const nonActiveUsers = data.filter((dt) => !dt.isActive);
  const emailVerifiedUsers = data.filter((dt) => dt.emailVerified || dt.isEmailVerified);
  const mobileVerifiedUsers = data.filter((dt) => dt.mobileVerified || dt.phoneVerified || dt.isMobileVerified);
  const blockedUsers = data.filter((dt) => dt.isBlocked || dt.blocked);
  const emailUnverifiedUsers = data.filter((dt) => !(dt.emailVerified || dt.isEmailVerified));
  const mobileUnverifiedUsers = data.filter((dt) => !(dt.mobileVerified || dt.phoneVerified || dt.isMobileVerified));
  const unblockedUsers = data.filter((dt) => !(dt.isBlocked || dt.blocked));
  const kycVerified = data.filter((dt) => dt.kycVerified);
  const kycNotVerified = data.filter((dt) => !dt.kycVerified);
  const kycPending = data.filter(
    (dt) =>
      dt.kyc.proofstatus == 2 ||
      dt.kyc.selfiestatus == 2 ||
      dt.kyc.panstatus == 2
  );
  const kycRejected = data.filter(
    (dt) =>
      dt.kyc.proofstatus == 0 ||
      dt.kyc.selfiestatus == 0 ||
      dt.kyc.panstatus == 0
  );
  console.log("kycPending", kycPending);
  // --- Enhanced Search and Filter Logic ---
  // Utility: Debounce hook for search input
  function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => setDebouncedValue(value), delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
  }

  // Add phone and other fields if needed
  const SEARCH_FIELDS = [
    "email", "username", "unique_id", "phone", "name"
  ];

  // Debounced search query for performance
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Modular filter logic
  const filteredData = () => {
    let filtered = [...data];

    // Status/KYC/Verification filter
    switch (active) {
      case "active":
        filtered = filtered.filter((dt) => dt.isActive);
        break;
      case "non-active":
        filtered = filtered.filter((dt) => !dt.isActive);
        break;
      case "emailVerified":
        filtered = filtered.filter((dt) => dt.emailVerified || dt.isEmailVerified);
        break;
      case "emailVerifiedInactive":
        filtered = filtered.filter((dt) => !(dt.emailVerified || dt.isEmailVerified));
        break;
      case "mobileVerified":
        filtered = filtered.filter((dt) => dt.mobileVerified || dt.phoneVerified || dt.isMobileVerified);
        break;
      case "mobileVerifiedInactive":
        filtered = filtered.filter((dt) => !(dt.mobileVerified || dt.phoneVerified || dt.isMobileVerified));
        break;
      case "blockedUsers":
        filtered = filtered.filter((dt) => dt.isBlocked || dt.blocked);
        break;
      case "blockedUsersInactive":
        filtered = filtered.filter((dt) => !(dt.isBlocked || dt.blocked));
        break;
      case "kycVerified":
        filtered = filtered.filter((dt) => dt.kycVerified);
        break;
      case "kycNotVerified":
        filtered = filtered.filter((dt) => !dt.kycVerified);
        break;
      case "kycPending":
        filtered = filtered.filter(
          (dt) =>
            dt.kyc.proofstatus == 2 ||
            dt.kyc.selfiestatus == 2 ||
            dt.kyc.panstatus == 2
        );
        break;
      case "kycRejected":
        filtered = filtered.filter(
          (dt) =>
            dt.kyc.proofstatus == 0 ||
            dt.kyc.selfiestatus == 0 ||
            dt.kyc.panstatus == 0
        );
        break;
      default:
        break;
    }

    // Date range filter
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filtered = filtered.filter((user) => {
        const created = new Date(user.createdAt);
        return created >= start && created <= end;
      });
    } else if (startDate) {
      const start = new Date(startDate);
      filtered = filtered.filter((user) => {
        const created = new Date(user.createdAt);
        return created >= start;
      });
    } else if (endDate) {
      const end = new Date(endDate);
      filtered = filtered.filter((user) => {
        const created = new Date(user.createdAt);
        return created <= end;
      });
    }

    // Enhanced search filter (multi-field, case-insensitive, partial)
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter((user) =>
        SEARCH_FIELDS.some((field) =>
          (user[field] ? user[field].toString().toLowerCase().includes(query) : false)
        )
      );
    }

    return filtered;
  };

  useEffect(() => {
    userList();
  }, []);
  useEffect(() => {
    if (isSuccess) {
      setData(userData.data);
    }
  }, [isSuccess]);

  const newUserList = filteredData();

  const columns = [
    { field: "id", headerName: "S.No", flex: 0.5, minWidth: 80, sortable: false },
    { field: "email", headerName: "Email", flex: 1, minWidth: 150, sortable: false },
    { field: "name", headerName: "Name", flex: 1, minWidth: 150, sortable: true },
    { field: "unique_id", headerName: "UID", flex: 0.75, minWidth: 120, sortable: true },
    {
      field: "created_at",
      headerName: "Register On",
      flex: 0.75,
      minWidth: 120,
      sortable: true,
      sortComparator: (v1, v2) => {
        // Compare as dates
        return new Date(v1) - new Date(v2);
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const statusClass =
          params.value === "Active" ? "text-green-500" : "text-yellow-500";
        return <span className={statusClass}>{params.value}</span>;
      },
    },
    {
      field: "kycVerified",
      headerName: "KYC Status",
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const statusClass =
          params.value ? "text-green-500" : "text-yellow-500";
        return <span className={statusClass}>{params.value.toString()}</span>;
      },
    },
    {
      field: "kycDate",
      headerName: "KYC Date",
      flex: 0.5,
      minWidth: 100,
      sortable: true,
      sortComparator: (v1, v2) => {
        // If value is '-', treat as null
        if (v1 === '-' && v2 === '-') return 0;
        if (v1 === '-') return 1;
        if (v2 === '-') return -1;
        // Try to parse as date, fallback to string
        const d1 = new Date(v1);
        const d2 = new Date(v2);
        if (!isNaN(d1) && !isNaN(d2)) return d1 - d2;
        return v1.localeCompare(v2);
      },
      renderCell: (params) => {
        const statusClass =
          params.value && params.value !== '-' ? "text-green-500" : "text-yellow-500";
        return <span className={statusClass}>{params.value.toString()}</span>;
      },
    },
    {
      field: "manage",
      headerName: "Manage",
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        return (
          <button
            onClick={() => {
              const id = params.row.userId;
              navigate(`/user/${id}`);
            }}
          >
            <FaEdit className={"text-black"} size={20} />
          </button>
        );
      },
    },
  ];

  // Mobile-optimized columns (hide less-critical fields)
  const columnsMobile = [
    { field: "id", headerName: "#", flex: 0.3, minWidth: 50, sortable: false },
    { field: "name", headerName: "Name", flex: 0.9, minWidth: 120, sortable: true },
    { field: "email", headerName: "Email", flex: 1.1, minWidth: 160, sortable: false },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      minWidth: 90,
      sortable: false,
      renderCell: (params) => {
        const statusClass = params.value === "Active" ? "text-green-500" : "text-yellow-500";
        return <span className={statusClass}>{params.value}</span>;
      },
    },
    {
      field: "manage",
      headerName: "",
      flex: 0.3,
      minWidth: 60,
      sortable: false,
      renderCell: (params) => {
        return (
          <button
            onClick={() => {
              const id = params.row.userId;
              navigate(`/user/${id}`);
            }}
            aria-label="Manage"
            title="Manage"
          >
            <FaEdit className={"text-black"} size={18} />
          </button>
        );
      },
    },
  ];

  const rows = [];
  {
    newUserList &&
      newUserList.forEach((item, index) => {
        const options = {
          year: "numeric",
          month: "long",
          day: "numeric",
        };

        const date = new Date(item.createdAt).toLocaleDateString(
          "en-US",
          options
        );
        const active = item.isActive ? "Active" : "Deactive";

        rows.push({
          id: index + 1,
          userId: item._id,
          email: item.email,
          name: item.username,
          unique_id: item.unique_id,
          created_at: date,
          status: active,
          kycVerified: item.kycVerified,
          kycDate: item?.kyc?.Date || "-",
        });
      });
  }

  const convertToAoA = (data) => {
    const headers = Object.keys(data[0]);

    const rows = data.map((obj) => headers.map((header) => obj[header]));

    return [headers, ...rows];
  };

  const exportFile = () => {
    const aoaData = convertToAoA(newUserList); // newUserList is already filtered
    const worksheet = XLSX.utils.aoa_to_sheet(aoaData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    XLSX.writeFile(workbook, "Users_List.xlsx");
  };
  console.log("block called");

  const style = {
    height: 100,
    width: 100,
    margin: "auto",
    alignItem: "center",
  };

  // console.log("gfg", newUserList);

  return (
    <>
      {isLoading ? (
        <Lottie animationData={loader} style={style} loop={true} width={20} />
      ) : (
        <div className="mx-auto w-full max-w-[100vw] px-2 sm:px-0">
          <div className="flex md:flex-row flex-col gap-4 pt-8">
            <div className="bg-white shadow-md shadow-gray-200 h-[200px] md:w-6/12 w-full rounded-lg flex flex-col p-2 mb-2 md:mb-0">
              <div>
                <p className="font-bold text-xl p-2 text-slate-500">Total Users</p>
                <p
                  onClick={() => {
                    setActive("all");
                    scrollingToTable();
                  }}
                  className="font-bold text-5xl p-2 pt-0 text-blue-900 cursor-pointer"
                >
                  {data.length}
                </p>
              </div>

              <div className="flex gap-8">
                <div className="px-2">
                  <p className="font-bold text-md pt-2  text-slate-500">
                    Active Users
                  </p>
                  <p
                    onClick={() => {
                      setActive("active");
                      scrollingToTable();
                    }}
                    className="font-bold text-lg  text-blue-900 cursor-pointer"
                  >
                    {activeUsers.length}
                  </p>
                </div>
                <div className="px-2">
                  <p className="font-bold text-md pt-2  text-slate-500">
                    Non-Active Users
                  </p>
                  <p
                    onClick={() => {
                      setActive("non-active");
                      scrollingToTable();
                    }}
                    className="font-bold text-lg  text-blue-900 cursor-pointer"
                  >
                    {nonActiveUsers.length}
                  </p>
                </div>

              </div>
            </div>

            {/* <div className="bg-white shadow-md shadow-gray-200 h-[200px] md:w-4/12 w-full rounded-lg flex flex-col p-2 mb-2 md:mb-0">
              <div>
                <p className="font-bold text-xl p-2 text-slate-500">Total Units</p>
                <p
                  onClick={() => {
                    setActive("all");
                    scrollingToTable();
                  }}
                  className="font-bold text-5xl p-2 pt-0 text-blue-900 cursor-pointer"
                >
                  {data.length}
                </p>
              </div>

              <div className="flex gap-8">
                <div className="">
                  <p className="font-bold text-md pt-2  text-slate-500">
                    Total units/day
                  </p>
                   <p
                    onClick={() => {
                      setActive("active");
                      scrollingToTable();
                    }}
                    className="font-bold text-lg  text-blue-900 cursor-pointer"
                  >
                    {activeUsers.length}
                  </p>
                </div>
                <div className="">
                  <p className="font-bold text-md pt-2  text-slate-500">
                    Total units/month
                  </p>
                  <p
                    onClick={() => {
                      setActive("non-active");
                      scrollingToTable();
                    }}
                    className="font-bold text-lg  text-blue-900 cursor-pointer"
                  >
                    {nonActiveUsers.length}
                  </p>
                </div>
              </div>
            </div> */}

            <div className="bg-white shadow-md shadow-gray-200 h-[200px] md:w-6/12 w-full rounded-lg flex flex-col p-2 mb-2 md:mb-0">
              <div>
                <p className="font-bold text-xl p-2 text-slate-500">
                  KYC Verification
                </p>
                <p
                  onClick={() => {
                    setActive("all");
                    scrollingToTable();
                  }}
                  className="font-bold text-5xl p-2 pt-0 text-blue-900 cursor-pointer"
                >
                  {data.length}
                </p>
              </div>

              <div className="flex gap-3">
                <div className="px-2">
                  <p className="font-bold text-md pt-2  text-slate-500">Verified</p>
                  <p
                    onClick={() => {
                      setActive("kycVerified");
                      scrollingToTable();
                    }}
                    className="font-bold text-lg  text-blue-900 cursor-pointer"
                  >
                    {kycVerified.length}
                  </p>
                </div>
                <div className="px-2">
                  <p className="font-bold text-md pt-2  text-slate-500">
                    Not-Verified
                  </p>
                  <p
                    onClick={() => {
                      setActive("kycNotVerified");
                      scrollingToTable();
                    }}
                    className="font-bold text-lg  text-blue-900 cursor-pointer"
                  >
                    {kycNotVerified.length}
                  </p>
                </div>
                <div className="px-2">
                  <p className="font-bold text-md pt-2  text-slate-500">Pending</p>
                  <p
                    onClick={() => {
                      setActive("kycPending");
                      scrollingToTable();
                    }}
                    className="font-bold text-lg  text-blue-900 cursor-pointer"
                  >
                    {kycPending.length}
                  </p>
                </div>
                <div className="px-2">
                  <p className="font-bold text-md pt-2  text-slate-500">
                    Rejected KYC
                  </p>
                  <p
                    onClick={() => {
                      setActive("kycRejected");
                      scrollingToTable();
                    }}
                    className="font-bold text-lg text-red-600 cursor-pointer"
                  >
                    {kycRejected.length}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Second row: three verification blocks */}
          <div className="flex md:flex-row flex-col gap-4 pt-4">
            {/* Email Verification Block */}
            <div className="bg-white shadow-md shadow-gray-200 h-[200px] md:w-4/12 w-full rounded-lg flex flex-col p-2 mb-2 md:mb-0">
              <div>
                <p className="font-bold text-xl p-2 text-slate-500">Email Verification</p>
                <p className="font-bold text-5xl p-2 pt-0 text-blue-900">{data.length}</p>
              </div>
              <div className="flex gap-6">
                <div className="px-2">
                  <p className="font-bold text-md pt-2 text-slate-500">Active</p>
                  <p onClick={() => { setActive("emailVerified"); scrollingToTable(); }} className="font-bold text-lg text-blue-900 cursor-pointer">{emailVerifiedUsers.length}</p>
                </div>
                <div className="px-2">
                  <p className="font-bold text-md pt-2 text-slate-500">Inactive</p>
                  <p onClick={() => { setActive("emailVerifiedInactive"); scrollingToTable(); }} className="font-bold text-lg text-yellow-600 cursor-pointer">{emailUnverifiedUsers.length}</p>
                </div>
              </div>
            </div>

            {/* Mobile Verification Block */
            }
            <div className="bg-white shadow-md shadow-gray-200 h-[200px] md:w-4/12 w-full rounded-lg flex flex-col p-2 mb-2 md:mb-0">
              <div>
                <p className="font-bold text-xl p-2 text-slate-500">Mobile Verification</p>
                <p className="font-bold text-5xl p-2 pt-0 text-blue-900">{data.length}</p>
              </div>
              <div className="flex gap-6">
                <div className="px-2">
                  <p className="font-bold text-md pt-2 text-slate-500">Active</p>
                  <p onClick={() => { setActive("mobileVerified"); scrollingToTable(); }} className="font-bold text-lg text-blue-900 cursor-pointer">{mobileVerifiedUsers.length}</p>
                </div>
                <div className="px-2">
                  <p className="font-bold text-md pt-2 text-slate-500">Inactive</p>
                  <p onClick={() => { setActive("mobileVerifiedInactive"); scrollingToTable(); }} className="font-bold text-lg text-yellow-600 cursor-pointer">{mobileUnverifiedUsers.length}</p>
                </div>
              </div>
            </div>

            {/* Blocked Users Block */}
            <div className="bg-white shadow-md shadow-gray-200 h-[200px] md:w-4/12 w-full rounded-lg flex flex-col p-2 mb-2 md:mb-0">
              <div>
                <p className="font-bold text-xl p-2 text-slate-500">Blocked Users</p>
                <p className="font-bold text-5xl p-2 pt-0 text-blue-900">{data.length}</p>
              </div>
              <div className="flex gap-6">
                <div className="px-2">
                  <p className="font-bold text-md pt-2 text-slate-500">Active</p>
                  <p onClick={() => { setActive("blockedUsers"); scrollingToTable(); }} className="font-bold text-lg text-red-600 cursor-pointer">{blockedUsers.length}</p>
                </div>
                <div className="px-2">
                  <p className="font-bold text-md pt-2 text-slate-500">Inactive</p>
                  <p onClick={() => { setActive("blockedUsersInactive"); scrollingToTable(); }} className="font-bold text-lg text-blue-900 cursor-pointer">{unblockedUsers.length}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex md:flex-row flex-col gap-4 pt-8">
            <div className="md:w-8/12 w-full h-[auto] shadow-md shadow-gray-200 rounded-lg overflow-hidden mb-2 md:mb-0" >
              <Graph />
            </div>
            <div className="md:w-4/12 w-full h-[fit-content] flex flex-col rounded-lg overflow-hidden relative">
              {/* Calendar input for selecting date (YYYY-MM-DD) */}
              <div className="mb-2 flex items-center gap-2 md:absolute md:top-2 md:right-2 md:mb-0 z-10 bg-white/80 backdrop-blur-sm px-2 py-1 rounded">
                <label htmlFor="pie-date-input" className="text-xs md:text-sm font-medium text-gray-700">Select Date:</label>
                <input
                  id="pie-date-input"
                  type="date"
                  value={selectedDate}
                  onChange={e => setSelectedDate(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded-md text-xs md:text-sm"
                  disabled={nftLoading}
                />
              </div>
              {nftLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Lottie animationData={loader} style={{ height: 100, width: 100 }} loop={true} />
                </div>
              ) : (
                <PieChart data={pieData} />
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-between mx-2 sm:mx-4 mt-20 mb-12 gap-4 sm:gap-0">
            <h1 className="font-bold text-2xl sm:text-3xl text-blue-900">Users List</h1>
            <div className="flex flex-col sm:flex-row gap-4 items-center w-full sm:w-auto">
              <div className="flex gap-2 items-center w-full sm:w-auto">
                <label htmlFor="start-date" className="text-sm text-gray-700">From:</label>
                <input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded-md text-sm w-full sm:w-auto"
                />
                <label htmlFor="end-date" className="text-sm text-gray-700">To:</label>
                <input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded-md text-sm w-full sm:w-auto"
                />
              </div>
              <div className="relative w-full sm:w-auto">
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-auto"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    title="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
              {/* Clear All Filters Button */}
              <button
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded-md text-sm font-medium"
                onClick={() => {
                  setActive("all");
                  setSearchQuery("");
                  setStartDate("");
                  setEndDate("");
                }}
                style={{ minWidth: 120 }}
              >
                Clear All Filters
              </button>
              <button
                className={`${styles.button} !w-fit flex gap-2 px-4 py-2 text-base rounded-md`}
                onClick={exportFile}
              >
                <BiExport className="" size={22} />
                Export
              </button>
            </div>
          </div>
          {/* Responsive DataGrid wrapper */}
          <div className="w-full overflow-x-auto rounded-lg shadow-md bg-white p-2 sm:p-4 mt-6">
            <div style={{ minWidth: 600 }}>
              <DataGrid
                ref={targetElement}
                rows={rows}
                columns={isMobile ? columnsMobile : columns}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: isMobile ? 5 : 10, page: 0 },
                  },
                }}
                pageSizeOptions={isMobile ? [5, 10] : [10, 25, 50]}
                density={isMobile ? "compact" : "standard"}
                sx={{
                  '& .MuiDataGrid-root': {
                    border: 'none',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #e5e7eb',
                    fontSize: isMobile ? '0.85rem' : '0.95rem',
                    padding: isMobile ? '6px 8px' : undefined,
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '0.9rem' : '1rem',
                  },
                  '& .MuiDataGrid-row': {
                    '&:nth-of-type(even)': {
                      backgroundColor: '#f9fafb',
                    },
                    '&:hover': {
                      backgroundColor: '#e0e7ff',
                    },
                  },
                  '& .MuiDataGrid-footerContainer': {
                    backgroundColor: '#f3f4f6',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    minHeight: isMobile ? '260px' : '300px',
                  },
                  borderRadius: 2,
                  boxShadow: 2,
                }}
                autoHeight
              />
            </div>
          </div>
        </div >
      )}
    </>
  );
};

export default Blocks;

